import {
	BuyerProjectInteractionTracking,
	tracking as barkTrackingLogEvent,
	EntityTracking,
	CsatWidget,
	sendContactRequest,
	tracking,
	_t,
} from '@bark/bark-core';
import ListHtmlBuilder from './list-html-builder';
import SkeletonManager from './skeleton-manager';
import BuyerApi from './buyer-api';
import BuyerNextBark from './buyer-next-bark';
import InitiateNewBark from './initiate-new-bark';
import TenderUpsellModal from './modals/tender-upsell-modal';
import {
	handleNextBarkInPostBarkModal,
	showSellersImmediately,
	toggleLoading,
} from './list-animations';
import { showAdditionalDetailsIfNeededAfterBark } from './after-bark-placed';
import {
	isRequestQuoteUpsellActive,
	canSetUpRequestQuoteUpsell,
	displayRequestQuoteUpsell,
	setUpRequestQuoteUpsell,
	removeRequestedSeller,
} from './../components/request-quote-upsell';

import ListUrlHandler from './list-url-handler';
import ListFilters from './list-filters';
import ListSortSelect from './list-sort';
import EventEmitter from './utils/event-emitter';

import ResponsePreference from './response-preference';
import UrgentRequest from './urgent-request';
import { handleEnquiry, trackEnquiries } from './enquiries';
import { handleBookNow, handleBookingCancellation } from './book-now';
import { showContactOptionsModal, showSendMessageModal } from './contact-options';
import { trackCallNow } from './call-now';
import EnquiriesSellersOnlyList from '../experiments/enquiries-sellers-separation/enquiries-sellers-only-list';
import EnquiriesSellersSeparationExpControllerV3 from '../experiments/enquiries-sellers-separation/controllerV3';
import {
	getRelatedCategoryCardData,
	buildRelatedCategoryCardHtml,
} from './related-category-card';
import { startNewBark } from './start-new-bark';


let setSeller = () => {};

export default async function (
	experimentName,
	state,
	isSimpleSeller = false,
	getBrowseProfessionalList = null,
) {
	const { Handlebars } = window;
	const experimentTracking = BuyerProjectInteractionTracking();
	const sellerHtmlBuilder = ListHtmlBuilder();
	const skeletonManager = SkeletonManager();
	const buyerApi = BuyerApi();

	const eventEmitter = new EventEmitter();
	const listFilters = await ListFilters(eventEmitter);
	const splitUrlHandler = ListUrlHandler(eventEmitter, listFilters);
	const listSortSelect = ListSortSelect(eventEmitter);

	const responsePreference = ResponsePreference();
	const urgentRequest = UrgentRequest();

	const csatWidget = new CsatWidget();

	const enquiriesSellersOnlyList = EnquiriesSellersOnlyList();
	const enquiriesSellersSeparationExpController =
		EnquiriesSellersSeparationExpControllerV3();

	/**
	 * @param {string} containerId
	 */
	const addManagedSkeletonContainer = (
		containerId = 'seller-list',
		initialCount = 10,
	) => {
		let container = {
			containerId: containerId,
			skeletonId: 'seller-list-card-skeleton',
			initialCount,
		};
		skeletonManager.addManagedContainer(container);
	};

	const projectHasMatches = () => {
		return state.matches?.sellerList?.length > 0;
	};

	const isDebug = () => new URL(window.location.href).searchParams.get('debug') === '1';

	const bookableTracking = new EntityTracking({ nameFormat: 'title', debug: isDebug() });

	let bookableSellerTracking = false;

	const hasBookableTracking = (callback) => {
		if (!callback) {
			callback = () => true;
		}
		return bookableSellerTracking && callback();
	};

	const handleEndState = () => {
		const buyerId = state?.buyerDetails?.data?.id;

		if (!buyerId || typeof buyerId === 'undefined') {
			bugsnagClient.notify(
				new Error('Failed to block Buyer: Could not determine the buyer user id'),
			);
		}

		try {
			return BuyerApi().blockBookableSellersForBuyer(
				buyerId,
				() => {
					setTimeout(() => {
						location.reload();
					}, 200);
				},
				(error) => {
					bugsnagClient &&
						bugsnagClient.notify(new Error('Error getting bookable sellers'), {
							metaData: { error: error },
						});
					return { data: [] };
				},
			);
		} catch (e) {
			bugsnagClient.notify(new Error('Error getting bookable sellers'), {
				metaData: { error: e },
			});
			return { data: [] };
		}
	};

	const ctaClick = (cta_name, sellerProfileId) => {
		if (hasBookableTracking()) {
			bookableTracking.fireTracking('list_click', {
				cta_name,
				...getSellerDataForTracking(sellerProfileId),
			});
		}
	};

	const triggerEndStateModal = (cta_name, sellerProfileId) => {
		ctaClick(cta_name, sellerProfileId);
		if (window.BuyerApp) {
			window.BuyerApp.launchModule('cta-modal', '__bookable-cta-modal', {
				type: 'generic',
				fireExpEvent: bookableTracking.fireTracking,
				updateTrackingEntity: bookableTracking.updateTrackingEntity,
				endState: handleEndState,
			});
		}
	};

	const respondToPendingInteractions = async () => {
		try {
			const result = await buyerApi.getPendingInteractions({
				projectIdHash: getProjectHash(),
			});

			if (!result?.data?.length) {
				return;
			}

			for (const item of result.data) {
				switch (item.attributes.type) {
					case 'quote':
						// Quote requested
						await buyerApi.postResponseActionPromise({
							projectId: item.attributes.project_id,
							sellerProfileId: item.attributes.seller_profile_id,
							type: item.attributes.type,
							source: item.attributes.source,
							bpvsid: item.attributes.metadata?.bpvsid,
							pendingInteractionId: item.id,
						});
						break;
					case 'contact':
						await sendContactRequest({
							projectId: item.attributes.project_id,
							sellerProfileId: item.attributes.seller_profile_id,
							bpvsId: item.attributes.metadata?.bpvsid,
							source: item.attributes.source,
							type: item.attributes.type,
							phone: item.attributes.metadata?.phone,
							timeframe: item.attributes.metadata?.timeframe,
							message: item.attributes.metadata?.message,
							pendingInteractionId: item.id,
						});
				}
			}
		} catch (e) {}
	};

	/**
	 * Once a project is loaded, we will fetch matches and responses to display to the viewer
	 * @param bpvid
	 */
	const getDataForSellerPageAndRender = async (bpvid) => {
		//Marketplace separated list start
		if (
			enquiriesSellersOnlyList.isFeatureFlagEnabled() &&
			!enquiriesSellersOnlyList.isExcludedCategory(
				state.projectDetail?.attributes?.category?.id,
			)
		) {
			if (!enquiriesSellersOnlyList.isExpandedList()) {
				await enquiriesSellersOnlyList.init(state);
				if (enquiriesSellersOnlyList.hasData(state)) {
					enquiriesSellersOnlyList.setIsShowing();
					enquiriesSellersOnlyList.renderList(state);
					renderRelatedCategoryCard('seller-list');

					experimentTracking.trackListView(
						'marketplace-seller-list',
						getMarketplaceSellersMetadata(state),
					);

					return;
				}
			}

			toggleSpinner(
				enquiriesSellersSeparationExpController.hasLoadingMsg(),
				enquiriesSellersSeparationExpController.getLoadingMsg(),
			);

			enquiriesSellersSeparationExpController.clearLoadingMsg();
		}
		//Marketplace separated list end

		//   get Page, and ListSort to pass onto the API. See function for full explanation
		let { p, ls, filterParams } = splitUrlHandler.getUrlParams();
		let fq = splitUrlHandler.buildFilterQuery(filterParams);
		splitUrlHandler.replaceUrlParamsInHistory({ p, ls, filterParams });

		await respondToPendingInteractions();

		let responsesPromise = getResponsesForProject({ bpvid });
		let matchesPromise = getMatchesForProject({
			bpvid,
			pageNum: p,
			sort: ls,
			version: 'v2',
			filterQuery: fq,
		});
		let bookableSellerPromise = getBookableSellersForProject();
		const sellerPromise = Promise.allSettled([
			matchesPromise,
			responsesPromise,
			bookableSellerPromise,
			p,
		]);

		Promise.all([sellerPromise, buyerApi.getSelfPromise()])
			.then(async (projectDetails) => {
				try {
					const isBookingExperimentOn =
						document.getElementById('ebb-enabled')?.value === '1';

					if (!isBookingExperimentOn) {
						return Promise.resolve(projectDetails);
					}

					const buyerId = projectDetails[1].data.id;
					const projectId = $('#_project-id').val();

					const { data: bookings } = await buyerApi.getProjectBookings(
						buyerId,
						projectId,
					);

					return Promise.resolve([...projectDetails, bookings]);
				} catch (err) {
					throw Error('could not find booking data');
				}
			})
			.then(handleSplitAndSortSellerListPromise)
			.catch(handleSellerListError)
			.finally(sellersPromiseFinallyHandler);
	};

	const getMarketplaceSellersMetadata = (state) => {
		let totalMarketplaceSellers = 0;
		let enquiryMarketplaceSellers = 0;
		let bookingMarketplaceSellers = 0;
		let messagesMarketplaceSellers = 0;

		state?.matches?.sellerList?.forEach((seller) => {
			let isMarketplaceSeller = false;

			if (seller.attributes?.match_types?.includes('marketplace-enquiry')) {
				enquiryMarketplaceSellers += 1;
				isMarketplaceSeller = true;
			}

			if (seller.attributes?.match_types?.includes('marketplace-booking')) {
				bookingMarketplaceSellers += 1;
				isMarketplaceSeller = true;
			}

			if (seller.attributes?.match_types?.includes('marketplace-messages')) {
				messagesMarketplaceSellers += 1;
				isMarketplaceSeller = true;
			}

			if (isMarketplaceSeller) {
				totalMarketplaceSellers += 1;
			}
		});

		return {
			marketplace_sellers: totalMarketplaceSellers > 0,
			no_marketplace_sellers: totalMarketplaceSellers,
			no_marketplace_enquiry: enquiryMarketplaceSellers,
			no_marketplace_booking: bookingMarketplaceSellers,
			no_marketplace_messages: messagesMarketplaceSellers,
		};
	};

	const getSellerAnalyticsMetadata = (sellerProfileId) => {
		const { projectDetail, buyerDetails } = state;
		const projectId = projectDetail.id;
		const buyerUserId = buyerDetails.data.id;
		const seller = state.matches?.originalSellerList?.find(
			(x) => x.id === sellerProfileId,
		);

		const marketplaceMatches = {
			'marketplace-enquiry': 'enquiry',
			'marketplace-booking': 'booking',
			'marketplace-messages': 'messages',
		};

		return {
			buyer_user_id: buyerUserId,
			project_id: projectId,
			seller_profile_id: sellerProfileId,
			list: enquiriesSellersOnlyList.isShowing() ? 'marketplace' : 'all',
			marketplace_ctas: (seller?.attributes?.match_types ?? [])
				.filter((x) => Object.keys(marketplaceMatches).includes(x))
				.map((x) => marketplaceMatches[x])
				.join(', '),
			source: 'seller-list',
		};
	};

	const getHashedSessionId = () => $('#sidh').val();

	const setProjectTitle = (project, elementId = '.js-project-title') => {
		let title = project.attributes?.title;
		$(elementId).text(title);
	};

	const showFirstViewUserFlow = () => {
		let oneDayAgo = new Date();
		oneDayAgo.setDate(oneDayAgo.getDate() - 1);
		let createdDate = state.projectDetail?.attributes?.created;
		if (!createdDate) {
			return false;
		}
		return (
			isFirstProjectView(state.projectDetail) &&
			new Date(createdDate.replace(/-/g, '/')) > oneDayAgo
		);
	};

	const isFirstProjectView = (projectDetails) => {
		return Boolean(projectDetails.attributes?.is_first_view);
	};

	const waitUntilDateTime = (dateTime, callback) => {
		let millisUntilTime = dateTime - new Date();
		if (millisUntilTime > 0) {
			setTimeout(callback, millisUntilTime);
		} else {
			callback();
		}
	};

	/**
	 * When project is loaded from API, it will contain data about active experiments for this project/buyer
	 * If they have any setup functionality needed, put it here
	 */
	const handleProjectDataLoadedExperiments = () => {
		if (
			isFirstProjectView(state.projectDetail) &&
			state.projectDetail?.attributes?.experiments
		) {
			state.projectDetail.attributes.experiments.map(({ name, variant }) => {
				experimentTracking.submitExpActWithVal(`Experiment first view - ${name}`, {
					project_id: getProjectHash(),
					variant,
				});
			});
		}
	};

	/**
	 * return the assigned variant from an experiment based on a slug
	 * @param {string} expName
	 * @returns {*|number} returns the variant ID, which will be 0 for control group and for non-allocations
	 */
	const getExperimentVariant = (expName = experimentName) => {
		let variant = 0;
		if (state.projectDetail?.attributes?.experiments) {
			const exp =
				state.projectDetail.attributes.experiments.find(
					(item) => item.name === expName,
				) || {};
			variant = exp.variant ?? 0;
		}
		return variant;
	};

	/**
	 * How should we load the list in
	 */
	const handleInitialLoading = (listElementId = 'seller-list') => {
		window.useNamespace(['buyer_seller-list']).then(function () {
			startSkeletonLoading(listElementId);
		});
	};

	/**
	 * @param {string} listElementId
	 */
	const startSkeletonLoading = (listElementId = 'seller-list') => {
		$('#' + listElementId).trigger('startSkeleton');
		toggleLoading(true);
	};

	const toggleSpinner = (show = false, message = null) => {
		if (show && message) {
			$('#seller-list-loader .loading-message').text(message);
			$('#seller-list-loader').removeClass('d-none');
		} else {
			$('#seller-list-loader').addClass('d-none');
		}
	};

	/**
	 * @param error
	 * @param page
	 * @param {string} listElementId
	 */
	const handleSellerListError = (
		error,
		page = 'seller list',
		listElementId = 'seller-list',
	) => {
		bugsnagClient.notify(new Error('Failed getting data for ' + page + ' page'), {
			metaData: error,
		});
		$('#' + listElementId).trigger('removeAllSkeletonsFromContainer');
	};

	const submitLoadingEventsForExperiments = (hasSuggestions) => {
		if (showFirstViewUserFlow() || isFirstProjectView(state.projectDetail)) {
			experimentTracking.submitExpAct(
				hasSuggestions ? 'has-suggestions' : 'no-suggestions',
			);
		}

		if (canSetUpRequestQuoteUpsell(getProjectHash())) {
			setUpRequestQuoteUpsell(state.matches.sellerList);
			$('#matches-list').data('bpvid', state.bpvid);
		}
	};

	const sellersPromiseFinallyHandler = () => {
		// only show next bark modal if theres no results.
		if (
			!state.matches?.sellerList?.length &&
			!state.responses?.sellerList?.length &&
			!state.matches?.totalMatches
		) {
			handleNextBarkInPostBarkModal({
				showFirstViewFlow: showFirstViewUserFlow(),
				project: state.projectDetail,
			});
		}
		// start tracking bookable sellers if not already
		setupBookableSellerTracking();
	};

	const redirectToUrlWithSplitListParams = (urlString) => {
		window.location.href = _l(splitUrlHandler.appendUrlParamsToUrl(urlString));
	};

	// Beginning of split seller list related functions
	const getMatchesForProject = ({
		bpvid,
		filter,
		pathname,
		search,
		version,
		sort,
		pageNum,
		filterQuery,
	}) => {
		const isMobileWeb = $(window).width() < 992;
		let options = {
			bpvid,
			filter,
			pathname,
			search,
			sort,
			version,
			filterQuery,
			isMobileWeb,
		};
		if (pageNum) {
			options.page = {
				number: pageNum,
			};
		}

		const sellerIdHash = getPublicProfileSellerIdHash(getProjectHash());
		if (sellerIdHash !== null) {
			options.filterQuery = options.filterQuery + '&psidh=' + sellerIdHash;
		}

		return buyerApi.getMatchesListForProject(getProjectHash(), options);
	};

	const slugify = (text) =>
		text
			.toString()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.trim()
			.replace(/\s+/g, '-')
			.replace(/[^\w-]+/g, '')
			.replace(/--+/g, '-');

	const remapBookableSellerCard = ({
		id,
		name,
		profile_text,
		location,
		avatar_url,
		is_elite,
		is_verified,
		labels,
		review_count,
		review_score,
	}) => ({
		id,
		type: 'bookable_sellers',
		attributes: {
			sp_id: -1,
			is_bark_vetted: is_verified,
			company_name: name,
			company_image: null,
			profile_text: profile_text,
			address_city: location,
			reviews_count: review_count,
			reviews_score: review_score,
			url_public_path:
				'/' +
				window.Bark.ENV.lang +
				'/' +
				window.Bark.ENV.locale +
				'/company/' +
				slugify(name) +
				'/p/' +
				id +
				'/',
			url_public:
				'https://' +
				window.Bark.ENV.web_hostname +
				'/' +
				window.Bark.ENV.lang +
				'/' +
				window.Bark.ENV.locale +
				'/company' +
				slugify(name) +
				'/p/' +
				id +
				'/',
			is_new: false,
			seller_responses: 10,
			last_online: '2024-06-12T13:08:32+00:00',
			last_online_text: '1 day ago',
			median_response_time: 79,
			median_response_time_text: '1 hour',
			avatar: {
				name: name,
				avatar_url: avatar_url,
				elite: is_elite,
				default_letter: name[0],
				default_class: 'jelly-bean',
				default_color: '#26848E',
			},
			price: null,
			labels: [
				{
					great_match: labels.includes('great_match'),
				},
				{
					quick_response: labels.includes('quick_to_respond'),
				},
				{
					is_new: false,
				},
				{
					is_bark_vetted: is_verified,
				},
			],
			top_categories: [],
			location_display_string: location,
			location_distance: null,
			location_distance_numeric: null,
			is_multi_location: false,
			is_online_only: false,
			is_no_location: false,
			match_type: 'marketplace-both',
			match_types: ['service-area', 'marketplace-booking', 'marketplace-enquiry'],
			bpvsid: null,
			quote_status: null,
			quote: null,
			hired_count: 0,
			has_declined: false,
			last_message: null,
			buyer_has_dismissed: false,
			project_response_id: null,
			formatted_last_message: null,
			formatted_quote: null,
			messenger_link: '',
		},
	});

	const getBookableSellersForProject = () => {
		const categoryId = state.projectDetail.attributes.category.id;
		const countryId = window.Bark.ENV.ccid;

		if (
			!categoryId ||
			typeof countryId === 'undefined' ||
			!countryId ||
			typeof countryId === 'undefined'
		) {
			bugsnagClient.notify(
				new Error(
					'Failed to get Bookable Sellers: Could not determine category and country id for project',
				),
			);
		}

		try {
			if (window?.bookable?.variant < 1) {
				return { data: [] };
			}
			return buyerApi.getBookableSellersForProject(
				categoryId,
				countryId,
				{},
				({ data }) => {
					const remappedData = data.map(remapBookableSellerCard);
					return remappedData;
				},
				(error) => {
					bugsnagClient &&
						bugsnagClient.notify(new Error('Error getting bookable sellers'), {
							metaData: { error: error },
						});
					return { data: [] };
				},
			);
		} catch (e) {
			bugsnagClient.notify(new Error('Error getting bookable sellers'), {
				metaData: { error: e },
			});
			return { data: [] };
		}
	};

	const getResponsesForBrowseList = ({ p, ls } = {}, success) => {
		return getBrowseProfessionalList(
			success,
			(error) => {
				bugsnagClient &&
					bugsnagClient.notify(new Error('Error on handling browse promise'), {
						metaData: { error: error },
					});
			},
			{
				page: { number: p },
				sort: ls ?? '-review',
			},
		);
	};

	const isMobileWeb = $(window).width() < 992;

	const getResponsesForProject = ({ bpvid, version }) => {
		return buyerApi.getResponseListForProject(getProjectHash(), {
			bpvid,
			version,
			isMobileWeb,
		});
	};

	const updateSCEPayloadState = (listType, payloadName, data, append) => {
		append ?? false;
		state.experimentData = state.experimentData || {};
		state.experimentData.sellerCardMultiVariant =
			state.experimentData.sellerCardMultiVariant || {};
		let experimentData = state.experimentData.sellerCardMultiVariant[listType] || {};
		if (append) {
			const currentPayload = experimentData[payloadName] || {};
			experimentData[payloadName] = {
				...currentPayload,
				...data,
			};
		} else {
			experimentData[payloadName] = data;
		}
		state.experimentData.sellerCardMultiVariant[listType] = experimentData;
	};

	const fireExperimentTrackingEvent = (
		eventName,
		listType,
		payloadLevel,
		additionalData,
	) => {
		const eventGroup = 'Seller Cards Multi Variant';
		const payloadTypes = ['experiment', 'buyer', 'project', 'list'];
		let appendData = additionalData || {};
		let payload = {};
		for (const payloadType of payloadTypes) {
			const ptp =
				state.experimentData?.sellerCardMultiVariant[listType][payloadType] || {};
			payload = { ...payload, ...ptp };
			if (payloadLevel === payloadType) {
				break;
			}
		}
		payload = {
			...payload,
			...appendData,
		};
		if (isDebug()) {
			console.log(eventGroup, eventName, payload);
		}
		tracking(eventGroup, eventName, payload);
	};

	const setSCEBuyerPayload = (listType) => {
		updateSCEPayloadState(
			listType,
			'buyer',
			{
				bes_token: window.Bark?.Env?.bes_token || null,
				buyer_id: state.buyerDetails?.data?.id,
				country_id: state.buyerDetails?.data?.country_id,
			},
			false,
		);
	};

	const setSCEProjectPayload = (listType) => {
		updateSCEPayloadState(listType, 'project', {
			category_id: state.projectDetail.attributes.category.id,
			category_name: state.projectDetail.attributes.category.name,
			project_id: state.projectDetail.id,
		});
	};

	const setSCEListPayload = (listType, total, page, sellers) => {
		let sellerFields = {};
		sellers.forEach(({ id }, index) => {
			sellerFields[`seller.${index}.hash`] = id;
		});
		updateSCEPayloadState(listType, 'list', {
			type: listType, // little redundant
			bpv_id: state.bpvid || null,
			total_count: total || 0,
			page,
			...sellerFields,
		});
	};

	const trackEliteProPriorityRanking = (meta, eventName) => {
		if (meta?.experiments?.elite_pro_priority_ranking) {
			tracking('Elite pro priority ranking', eventName, {
				variant: meta.experiments.elite_pro_priority_ranking.variant,
			});
		}
	};

	const responsesPromiseHandler = ({ data, meta }) => {
		const listType = 'responses';
		setSCEBuyerPayload(listType);
		setSCEProjectPayload(listType);
		const reactCardsVariant = checkForReactCardsVariant(meta, listType);
		state.responses = {
			reactCardsVariant,
			sellerList: data,
		};
		setSCEListPayload(listType, data.length, 0, data);
		fireExperimentTrackingEvent('View', listType, 'list');
	};

	let sellersTrackingData = {};

	const bookableSellersPromiseHandler = ({ data }) => {
		const remappedData = data.map(remapBookableSellerCard);
		const count = remappedData.length;
		state.matches.totalMatches = state.matches.totalMatches + count;
		state.matches.sellerList = [...remappedData, ...state.matches.sellerList];
		state.matches.originalSellerList = state.matches.sellerList;
		listFilters.setPaginationModifier(count);
		listFilters.draw();
		const variant = window.bookable?.variant !== null ? window.bookable.variant : null;
		setupBookableSellerTracking(variant);
	};

	const setupBookableSellerTracking = (variant) => {
		if (Object.keys(sellersTrackingData).length > 0) {
			return;
		}
		sellersTrackingData = {};
		let sellerListTrackingData = {};
		let bookableCount = 0;
		for (let i in state.matches.sellerList) {
			const seller = state.matches.sellerList[i];
			const isBookable = ['booking-request'].some((r) =>
				seller.attributes?.match_types.includes(r),
			);
			if (isBookable) {
				bookableSellerTracking = true;
				bookableCount++;
			}
			const isFacade = seller.type === 'bookable_sellers';
			let labels = [];
			for (let j in seller.attributes.labels) {
				for (let label of Object.keys(seller.attributes.labels[j])) {
					if (seller.attributes.labels[j][label]) {
						labels.push(label);
					}
				}
			}
			let index = parseInt(i, 10);
			index = Number.isNaN(index) ? null : index + 1;
			sellersTrackingData[seller.id] = {
				'seller_card.hash_or_uuid': seller.id,
				'seller_card.position_in_list': index,
				'seller_card.is_bookable': isBookable,
				'seller_card.is_f': isFacade,
				'seller_card.name': seller.attributes.company_name,
				'seller_card.avatar_url': seller.attributes.avatar.avatar_url,
				'seller_card.elite': seller.attributes.avatar.elite,
				'seller_card.is_verified': seller.attributes.is_bark_vetted,
				'seller_card.review_score': seller.attributes.reviews_score,
				'seller_card.review_count': seller.attributes.reviews_count,
				'seller_card.labels': labels.join(','),
			};
			let prefix = `seller.${i}.`;
			sellerListTrackingData[prefix + 'hash_or_uuid'] = seller.id;
			sellerListTrackingData[prefix + 'is_bookable'] = isBookable;
			sellerListTrackingData[prefix + 'f'] = isFacade;
		}

		if (!bookableSellerTracking) {
			return;
		}
		let entityDefs = [
			{
				id: 'buyer',
				data: {
					bes_token: window.Bark?.ENV?.bes_token || null,
					buyer_id: state?.buyerDetails?.data?.id,
					country_id: window.Bark?.ENV?.ccid,
				},
			},
			{
				id: 'project',
				data: {
					project_id: state?.projectDetail?.id,
					category_id: state.projectDetail?.attributes?.category?.id,
				},
			},
			{
				id: 'list',
				data: {
					type: 'matches',
					bpv_id: state.bpvid,
					total_count: state.matches.totalMatches,
					page: 1,
					count: state.matches?.sellerList?.length,
					bookable_count: bookableCount,
					...sellerListTrackingData,
				},
			},
		];
		if (typeof variant !== 'undefined' && variant !== null) {
			entityDefs.push({
				id: 'experiment',
				data: { variant },
			});
		}
		bookableTracking.createEvents([
			{
				id: 'list_loaded',
				name: 'List View',
				group: 'Bookable Seller Profile',
				entities: ['experiment', 'buyer', 'project', 'list'],
			},
			{
				id: 'list_click',
				name: 'List CTA Click',
				group: 'Bookable Seller Profile',
				entities: ['experiment', 'buyer', 'project', 'list'],
			},
		]);
		bookableTracking.createEntities(entityDefs);
		bookableTracking.fireTracking('list_loaded');
	};

	const getSellerDataForTracking = (sellerId) => {
		try {
			return sellersTrackingData[sellerId];
		} catch (e) {
			return { hash_or_uuid: sellerId };
		}
	};

	/**
	 * store matches and pagination data into state
	 * @param data
	 * @param meta
	 * @param links
	 */
	const matchesPromiseHandler = ({ data, meta, links }) => {
		trackEliteProPriorityRanking(meta, 'Matches tab loaded');

		let totalPages = meta?.pagination?.total_pages || 0;
		let currentPage = meta?.pagination?.current_page || 0;
		let totalMatches = meta?.pagination?.total || 0;

		const listType = 'matches';
		setSCEBuyerPayload(listType);
		setSCEProjectPayload(listType);
		const reactCardsVariant = checkForReactCardsVariant(meta, listType);
		state.matches = {
			reactCardsVariant,
			sellerList: data,
			originalSellerList: data,
			pagination: {
				nextLink: totalPages > 1 && currentPage < totalPages ? links?.next : null,
				prevLink: totalPages > 1 && currentPage < totalPages ? links?.prev : null,
				currentPage,
				nextPageNumber: totalPages > currentPage ? currentPage + 1 : null,
				prevPageNumber: currentPage > 1 ? currentPage - 1 : null,
				totalPages,
			},
			totalMatches,
		};
		setSCEListPayload(listType, totalMatches, currentPage, data);
		fireExperimentTrackingEvent('View', listType, 'list');
		if (window.hj && reactCardsVariant > 0) {
			window.hj('trigger', 'multivariate_cards_on');
		}
	};

	const checkForReactCardsVariant = (meta, listType) => {
		let viewVariant = null;
		let justAllocated = false;
		const url = new URL(window.location.href);
		const override = url.searchParams.get('scv');
		if (typeof override !== 'undefined' && override !== null) {
			viewVariant = Number(override);
			updateSCEPayloadState(listType, 'experiment', {
				variant: viewVariant,
				view_variant: viewVariant,
				forced_experiment: true,
			});
		} else if (meta?.experiments?.seller_cards_multi_variant) {
			const {
				variant,
				view_variant: expViewVariant,
				allocated,
			} = meta.experiments.seller_cards_multi_variant;
			viewVariant = expViewVariant ? expViewVariant : 0;
			justAllocated = allocated;
			updateSCEPayloadState(listType, 'experiment', {
				variant,
				view_variant: viewVariant,
				forced_experiment: false,
			});
		}
		if (typeof viewVariant !== 'undefined') {
			// fire heap allocation / view event
			if (justAllocated) {
				fireExperimentTrackingEvent('Allocation', listType, 'project', {
					type: listType,
				});
			}
		}
		return viewVariant;
	};

	/**
	 *
	 * @param p page number
	 * @param lt list active Tab identifier
	 * @param ls list active Sort identifier
	 * @param filterParams
	 */
	const loadAndRenderSplitPanel = ({ p, lt, ls, filterParams }) => {
		let targetId = `${lt}-list`;
		const initialReactVariant = state[lt].reactCardsVariant > 0;
		if (initialReactVariant) {
			targetId = `__${targetId}`;
			const el = $(`#${targetId}`);
			const prev = el.prev();
			el.remove();
			skeletonManager.removeContainer(targetId);
			if (lt === 'matches') {
				prev.after('<div class="seller-list" id="__matches-list"></div>');
			} else {
				prev.after(
					'<div class="seller-list has-responses my-2" id="__responses-list"></div>',
				);
			}
		}
		startSkeletonForTab({ targetId });
		$(`#${lt}`).tab('show');
		if (lt === 'responses') {
			let listPromise = getResponsesForProject({ bpvid: state.bpvid });
			const projectId = $('#_project-id').val();
			listPromise.then((arg) => {
				responsesPromiseHandler(arg);
				let target = $(`#${targetId}`);
				if (initialReactVariant && !state?.responses?.reactCardsVariant > 0) {
					target.trigger('replaceSkeletonWithContent', []);
					targetId = `${lt}-list`;
					startSkeletonForTab({ targetId });
				} else if (!initialReactVariant && state?.responses?.reactCardsVariant > 0) {
					target.trigger('replaceSkeletonWithContent', []);
					targetId = `__${targetId}`;
					startSkeletonForTab({ targetId });
				}
				replaceSkeletonForTab(
					{
						targetId,
						sellerList: state.responses.sellerList,
						isResponsesList: true,
						reactCardsVariant: state?.responses?.reactCardsVariant,
						listType: 'responses',
					},
					sellerHtmlBuilder,
				);
			});
		}
		if (lt === 'matches') {
			let fq = splitUrlHandler.buildFilterQuery(filterParams);
			let args = {
				bpvid: state.bpvid,
				pageNum: p || 1,
				sort: ls || 'best',
				version: 'v2',
				filterQuery: fq,
			};
			eventEmitter.emit('sellerlistloading.start');
			let matchesPromise = getMatchesForProject(args);
			listSortSelect.setSelectedSort(args.sort);

			matchesPromise.then((matchesPromiseData) => {
				matchesPromiseHandler(matchesPromiseData);
				if (initialReactVariant && !state?.responses?.reactCardsVariant > 0) {
					let target = $(`#${targetId}`);
					target.trigger('removeAllSkeletonsFromContainer');
					startSkeletonForTab({ targetId: `${lt}-list` });
				} else if (!initialReactVariant && state?.responses?.reactCardsVariant > 0) {
					let target = $(`#${targetId}`);
					target.trigger('removeAllSkeletonsFromContainer');
					startSkeletonForTab({ targetId: `__${lt}-list` });
				}
				handleShowingRequestQuoteFromAll({
					sellerList: state.matches.sellerList,
					p: args.pageNum,
					lt,
					ls: args.sort,
					filterParams,
				});
				renderPaginationRowForTab(lt);
				$('.js-total-matches-count').text(state.matches.totalMatches);
				replaceSkeletonForTab(
					{
						targetId,
						sellerList: state.matches.sellerList,
						isResponsesList: false,
						reactCardsVariant: state?.matches?.reactCardsVariant,
						listType: 'matches',
					},
					'project_sellers',
				);
				trackEnquiries(state?.projectDetail?.id, state.matches.sellerList, args.pageNum);
				//Update filters data
				listFilters.updateData(matchesPromiseData);
				//Re-draw filters
				listFilters.draw();
				eventEmitter.emit('sellerlistloading.finish');
			});
		}
	};

	const getPublicProfileSellerIdHash = (projectIdHash) => {
		let sellerIdHash = window.Bark.get_cookie(`fs_${projectIdHash}`);
		if (sellerIdHash == null || sellerIdHash === 'null') {
			const foundMatch = window.Bark.get_cookie('fs_mffs');
			//only set if seller is eligible
			if (foundMatch === 'y') {
				sellerIdHash = window.Bark.get_cookie('publicProfileIdhBarkSrc');
				//reset generic cookie and add to specific cookie for 30 days
				window.Bark.set_cookie(`fs_${projectIdHash}`, sellerIdHash, 30, '/');
			}
			window.Bark.clear_cookie('publicProfileIdhBarkSrc', '/');
			window.Bark.clear_cookie('fs_mffs', '/');
		}

		if (sellerIdHash == null || sellerIdHash === 'null') {
			return null;
		}
		return sellerIdHash;
	};

	const handleProjectFromSellerProfile = () => {
		if (hasProjectFromSellerProfile()) {
			const requestFromSellerId = window.Bark.get_cookie('requestQuoteFromSeller');
			const sellerRow = state.matches.sellerList.find(
				(o) => o.type === 'project_sellers' && o.id === requestFromSellerId,
			);
			if (sellerRow != null) {
				if (window.BuyerApp) {
					window.BuyerApp.launchModule('request-reply-modal', '__request-reply-modal', {
						hashedProjectId: getProjectHash(),
						bpvsId: sellerRow.bpvsid,
						sellerId: requestFromSellerId,
						buyerPhone: state.buyerDetails?.data?.telephone,
						onSuccess: (r) => requestReplyRqSuccess(r, requestFromSellerId),
						onClose: () => displayRequestQuoteUpsell(getProjectHash()),
						//onError,
					});
				}
			}
			window.Bark.clear_cookie('requestQuoteFromSeller', '/');
		}
	};

	const hasProjectFromSellerProfile = () => {
		const requestFromSellerId = window.Bark.get_cookie('requestQuoteFromSeller');
		return Boolean(requestFromSellerId && requestFromSellerId !== 'null');
	};

	const handleGreatMatchInfoClick = (projectId, seller_profile_id) => {
		tracking('seller-list-&-profile', 'enquiries - info icon select', {
			projectId,
			seller_profile_id,
		});
	};

	const handleBackButtonClick = () => {
		const { projectDetail, buyerDetails } = state;
		const projectId = projectDetail.id;
		const buyerUserId = buyerDetails.data.id;

		tracking('marketplace-seller-list', 'back - click', {
			project_id: projectId,
			buyer_user_id: buyerUserId,
			from_first_list: enquiriesSellersOnlyList.isExpandedList(),
		});
	};

	/**
	 *  Passes through data to the request quote from all module with sensible defaults based on state
	 * @param sellerList the seller list to pass in (if null, uses state.fullSellerList)
	 * @param project project.attributes to pass in (if null uses state.projectDetails.attributes)
	 * @param buyer buyer data to pass in, if null uses state.buyerDetails
	 * @param p int? current page integer optional
	 * @param lt  current tab string optional
	 * @param ls current sort string optional
	 */
	const handleShowingRequestQuoteFromAll = ({
		sellerList,
		project,
		buyer,
		p,
		lt,
		ls,
		filterParams,
	}) => {
		const args = {
			sellerList: sellerList ?? state.fullSellerList,
			project: project ?? state?.projectDetail?.attributes,
			buyer: buyer ?? state.buyerDetails,
			projectId: getProjectHash(),
			p,
			lt,
			ls,
			filterParams,
		};
		if (sellerList.filter((o) => o.type === 'bookable_sellers').length === 0) {
			window.rqfa.handleShowingRQFA(args);
		}
	};

	const setSelectedSort = (sortParamString) => {
		const sortSelected = $(`.js-sort-option[value='${sortParamString}']`);
		if (sortSelected.length) {
			$('.js-sort-text').text(sortSelected.text());
		}
	};

	const renderPaginationRowForTab = (containerId) => {
		const paginationRowContainer = $(`#${containerId}-tab .pagination-row-container`);
		let paginationTemplate = Handlebars.compile($('#pagination-row').html());
		paginationRowContainer.html(
			paginationTemplate({
				nextPageNumber: state.matches?.pagination?.nextPageNumber,
				prevPageNumber: state.matches?.pagination?.prevPageNumber,
				currentPage: state.matches?.pagination?.currentPage,
				totalPages: state.matches?.pagination?.totalPages,
			}),
		);
	};

	/**
	 *  ENTRY POINT for split list page rendering
	 *  Expects an array from a Promise.all for Matches + Responses, and then an object from buyerSelf
	 *  This is only used for initial page load, and it passes off everything to helper functions
	 * @param matchesPromiseData sellerList data + meta and Links data since matches have pagination/sorting
	 * @param responsesPromiseData Just a list of sellers
	 * @param buyerSelf buyer/self response obj
	 */
	const handleSplitAndSortSellerListPromise = ([
		[matchesPromiseResult, responsesPromiseResult, bookableSellerPromiseResult, pageNum],
		buyerSelf,
		bookings,
	]) => {
		const matchesPromiseData = matchesPromiseResult.value;
		const responsesPromiseData = responsesPromiseResult.value;
		let bookableSellerPromiseData = { data: [] };
		if (bookableSellerPromiseResult.status !== 'rejected') {
			bookableSellerPromiseData = bookableSellerPromiseResult.value;
		}
		// Create a new URL object
		const url = new URL(window.location.href);
		const buyerFeedbackQueryParam = url.searchParams.get('fthx');
		const bookingId = url.searchParams.get('cbid');
		const bookingFeedback = url.searchParams.get('bbfb');
		url.searchParams.delete('fthx');
		url.searchParams.delete('cbid');
		url.searchParams.delete('bbfb');
		history.replaceState(history.state, '', url.href);

		state.buyerDetails = buyerSelf;
		state.bookings = bookings;
		state.showBuyerFeedbackModal = buyerFeedbackQueryParam === '1';

		responsesPromiseHandler(responsesPromiseData);
		matchesPromiseHandler(matchesPromiseData);
		bookableSellersPromiseHandler(bookableSellerPromiseData);

		handleProjectFromSellerProfile();

		//Set buyer country id for filters
		listFilters.setBuyerCountryId(buyerSelf);
		//Update filters data
		listFilters.updateData(matchesPromiseData);

		renderSplitSellerListPage();

		//Re-draw filters
		listFilters.draw();
		//Set experiments on RQFA's state
		window.rqfa.setExperiments(state.projectDetail?.attributes?.experiments);

		handleShowingRequestQuoteFromAll({
			sellerList: state.matches.sellerList,
			...splitUrlHandler.getUrlParams(),
		});
		submitLoadingEventsForExperiments(matchesPromiseData.data.length > 0);
		bindSplitListListeners();
		// gotta enable the new bark button because it was rendered after page load
		InitiateNewBark().bind();

		if (state.projectDetail.attributes?.show_response_preference) {
			if (isFirstProjectView(state.projectDetail)) {
				experimentTracking.submitExpActWithVal(
					'Experiment first view - response_preference',
					{ variant: 1 },
				);
			}
			responsePreference.init(
				state.projectDetail.id,
				state.projectDetail.attributes.created,
			);
		} else {
			if (isFirstProjectView(state.projectDetail)) {
				experimentTracking.submitExpActWithVal(
					'Experiment first view - response_preference',
					{ variant: 0 },
				);
			}
		}

		if (state.projectDetail.attributes?.show_urgent_request) {
			if ($('#urgent-request-mobile-template').length) {
				// If not in the intent/urgent experiment (variant 2)
				if (isFirstProjectView(state.projectDetail)) {
					experimentTracking.submitExpActWithVal(
						'Experiment first view - urgent_request',
						{ variant: 1 },
					);
				}
				urgentRequest.init(
					state.projectDetail.id,
					state.projectDetail.attributes.created,
				);
			}
		} else {
			if (isFirstProjectView(state.projectDetail)) {
				experimentTracking.submitExpActWithVal('Experiment first view - urgent_request', {
					variant: 0,
				});
			}
		}

		csatWidget.init();

		// BarkVetted tooltip
		$('.bark-vetted-info').tooltip({
			title: _t('seller_profile:tooltip.bark-vetted-badge'),
			template:
				'<div class="tooltip" role="tooltip"><div class="arrow"></div><div class="tooltip-inner text-xs" style="max-width: 240px;"></div></div>',
			trigger: 'hover focus',
		});

		function trackEmailFeeedback() {
			function getBookingFeedback() {
				switch (bookingFeedback) {
					case 'okh':
					case 'okc':
						return 'spoke';
					case 'nokb':
						return 'no_buyer_pickup';
					case 'noks':
						return 'no_seller_call';
					case 'na':
						return 'no_feedback';
					default:
						return undefined;
				}
			}

			barkTrackingLogEvent(
				'buyer_booking_callback_feedback_email',
				'Buyer-booking-call-feedback',
				{
					buyer_user_id: state?.buyerDetails.data.id,
					project_id: state?.projectDetail?.id,
					booking_id: bookingId,
					'feedback option': getBookingFeedback(),
				},
			);
		}

		state.showBuyerFeedbackModal && trackEmailFeeedback();

		trackEnquiries(state?.projectDetail?.id, state?.matches?.sellerList, pageNum);
		trackCallNow(
			state?.projectDetail?.id,
			state?.matches?.sellerList,
			pageNum,
			state.buyerDetails.data.id,
		);
		experimentTracking.trackListView(
			'seller-list-&-profile',
			getMarketplaceSellersMetadata(state),
		);
	};

	const buildSplitTabHtml = () => {
		try {
			const tabbedNavTemplate = Handlebars.compile($('#tabbed-nav-template').html());
			const responsesAreOpen =
				state.projectDetail.attributes.state.is_open &&
				!state.projectDetail.attributes.state.filled &&
				!state.projectDetail.attributes.state.is_old;

			const provider_noun_plural = state.projectDetail.attributes.provider_noun_plural;

			return tabbedNavTemplate({
				responsesAreOpen,
				response_count: state.responses.sellerList.length,
				match_count: state.matches.sellerList.length,
				provider_noun_plural,
				project_is_closed: !state.projectDetail.attributes.state.is_open,
				projectId: getProjectHash(),
				responses_provider_noun_plural:
					state.responses.sellerList.length === 1
						? state.projectDetail.attributes.provider_noun
						: provider_noun_plural,
				hasFiltersApplied: listFilters.getCurrentFiltersAppliedCount() > 0,
			});
		} catch (e) {}
	};

	// The design places the card directly after the second seller card (Figma 90:27002)
	const RELATED_CATEGORY_CARD_POSITION = 2;

	/**
	 * Inserts the card into the matches list after the second seller card (or after the
	 * last one when fewer are shown). The React cards variant mounts asynchronously, so
	 * retry briefly until the list has children.
	 *
	 * @returns {Promise<jQuery|null>} the inserted card element, or null when it couldn't be placed
	 */
	const insertRelatedCategoryCard = (cardHtml, matchesTargetId, attemptsLeft = 20) => {
		const $sellerCards = $(`#${matchesTargetId}`).children(':not(.skeleton-content)');
		if (!$sellerCards.length) {
			if (attemptsLeft <= 0 || !$(`#${matchesTargetId}`).length) {
				return Promise.resolve(null);
			}
			return new Promise((resolve) => {
				setTimeout(
					() => resolve(insertRelatedCategoryCard(cardHtml, matchesTargetId, attemptsLeft - 1)),
					250,
				);
			});
		}
		const $card = $(cardHtml);
		const insertAfterIndex = Math.min(RELATED_CATEGORY_CARD_POSITION, $sellerCards.length) - 1;
		$sellerCards.eq(insertAfterIndex).after($card);
		return Promise.resolve($card);
	};

	/**
	 * Fetches the primary related-category selection for this project and renders the
	 * card into the matches list. Eligibility is decided server-side — the api returns
	 * no category for ineligible projects. Fires the impression event once the card is
	 * actually in the DOM, and wires the CTA/skip controls.
	 */
	const renderRelatedCategoryCard = (matchesTargetId) => {
		const hashedProjectId = getProjectHash();
		if (!hashedProjectId) {
			return;
		}

		Promise.resolve(buyerApi.getPrimaryRelatedCategory(hashedProjectId))
			.then((resp) => {
				const data = getRelatedCategoryCardData(resp);
				if (!data) {
					return null;
				}

				// Per-category marketing copy keyed by slug, falling back to the generic strings
				const copyKeys = (field) => [
					`buyer_seller-list:related-category.categories.${data.categorySlug}.${field}`,
					`buyer_seller-list:related-category.${field}`,
				];

				const cardHtml = buildRelatedCategoryCardHtml({
					categoryId: data.categoryId,
					eyebrow: _t('buyer_seller-list:related-category.eyebrow'),
					timeEstimate: _t('buyer_seller-list:related-category.time-estimate'),
					title: _t(copyKeys('title'), {
						categoryName: data.categoryName,
						interpolation: { escapeValue: false },
					}),
					subtitle: _t(copyKeys('subtitle')),
					ctaLabel: _t(copyKeys('cta'), {
						categoryName: data.categoryName,
						interpolation: { escapeValue: false },
					}),
					skipLabel: _t('buyer_seller-list:related-category.skip'),
				});
				if (!cardHtml) {
					return null;
				}

				return insertRelatedCategoryCard(cardHtml, matchesTargetId).then(($card) => {
					if (!$card) {
						return null;
					}

					const trackParams = {
						project_id: hashedProjectId,
						category_id: data.categoryId,
						variant: data.variant,
					};

					barkTrackingLogEvent(
						'seller-list-&-profile',
						'Related Category - Impression',
						trackParams,
					);

					$card.on('click', '.js-related-category-start', (e) => {
						e.preventDefault();
						barkTrackingLogEvent(
							'seller-list-&-profile',
							'Related Category - Clicks Start Lead',
							trackParams,
						);
						// Open the category page in a new tab; nrfp=true starts the NRF on
						// load (bark-core). No slug from the api -> fall back to in-page flow.
						if (data.categorySlug) {
							const nrfParams = new URLSearchParams({
								nrfp: 'true',
								bark_mode: 'related-category-selection',
							});

							// First bark's location prefills the NRF's location step;
							// prefilled_loc marks it a suggestion, not an answer (BBQ-7137)
							if (data.location) {
								nrfParams.set('locid', data.location.id);
								nrfParams.set('pt', data.location.type);
								nrfParams.set('locname', data.location.name ?? '');
								nrfParams.set('prefilled_loc', 'true');
							}
							window.open(
								`${_l(`/${data.categorySlug}/`)}?${nrfParams}`,
								'_blank',
								'noopener',
							);
						} else {
							startNewBark({
								categoryId: $(e.currentTarget).data('category-id'),
								barkMode: 'related-category-selection',
							});
						}
					});

					$card.on('click', '.js-related-category-skip', (e) => {
						e.preventDefault();
						barkTrackingLogEvent('seller-list-&-profile', 'Related Category - Skip', trackParams);
						$card.remove();
					});

					return $card;
				});
			})
			.catch((e) => {
				bugsnagClient.notify(e);
			});
	};

	/**
	 * This does all the groundwork for rendering the split version of the page, once the data has been fetched from API
	 */
	const renderSplitSellerListPage = () => {
		toggleLoading(false);
		// do some DOM manipulation -- we should clean this up in future
		$('#project-seller-list').removeClass('bg-grey-50').addClass('bg-white');
		// move BNB section a bit
		let $bnb = $('.default-seller-list-container .next-bark-wrapper');
		// then put in our shiny new split tab list
		$('.seller-list-sort-container').removeClass('d-none').html(buildSplitTabHtml());

		$('.js-total-matches-count').text(state.matches.totalMatches);
		// and put the BNB after the tabs...
		$('#seller-list-sorting-content').append($bnb);

		BuyerNextBark({ barkMode: 'bnb-project-dash' }).getNextBarksAndAppend(
			$('#next-bark-carousel'),
			$('#next-bark-carousel-mobile'),
		);
		$('.default-seller-list-container').remove();

		const feedbackModal = $('#feedback-gratitude-modal');
		state.showBuyerFeedbackModal && feedbackModal.modal('show');

		const matchesReactCardsVariant = state?.matches?.reactCardsVariant ?? 0;
		const matchesTarget =
			matchesReactCardsVariant > 0 ? '__matches-list' : 'matches-list';
		startSkeletonForTab({ targetId: matchesTarget });
		replaceSkeletonForTab({
			targetId: matchesTarget,
			sellerList: state.matches.sellerList,
			isResponsesList: false,
			reactCardsVariant: matchesReactCardsVariant,
			listType: 'matches',
		});

		if (matchesReactCardsVariant === 0) {
			renderRelatedCategoryCard(matchesTarget);
		}
		const responsesReactCardsVariant = state?.responses?.reactCardsVariant ?? 0;
		const responsesTarget =
			responsesReactCardsVariant > 0 ? '__responses-list' : 'responses-list';
		startSkeletonForTab({ targetId: responsesTarget });
		replaceSkeletonForTab({
			targetId: responsesTarget,
			sellerList: state.responses.sellerList,
			isResponsesList: true,
			reactCardsVariant: responsesReactCardsVariant,
			listType: 'responses',
		});

		renderPaginationRowForTab('matches');

		let { lt, ls } = splitUrlHandler.getUrlParams();
		$(`#${lt}`).tab('show');
		// todo ben remove this
		if (ls !== 'best') {
			setSelectedSort(ls);
		}

		listSortSelect.setBuyerCountryId(state.buyerDetails);
		listSortSelect.setSelectedSort(ls);

		$('#seller-list').trigger('removeAllSkeletonsFromContainer');
	};

	const scrollToTopOfSellerList = () => {
		$('html, body').animate(
			{
				scrollTop: $('#project-seller-list').offset().top,
			},
			500,
		);
	};

	const scrollToTopOfSellerListMobileTabletOnly = () => {
		if ($(window).width() < 992) {
			$('html, body').animate(
				{
					scrollTop: $('#project-seller-list').offset().top,
				},
				500,
			);
		}
	};

	/**
	 * Just triggers startSkeleton on a target ID, but specifically with 10 rows
	 * @param targetId ID of a seller-list container element
	 */
	const startSkeletonForTab = ({ targetId }) => {
		let target = $(`#${targetId}`);
		target.empty();
		if (!skeletonManager.isThisContainerManaged(targetId)) {
			skeletonManager.addManagedContainer({
				containerId: targetId,
				skeletonId: 'seller-list-card-skeleton',
				initialCount: 10,
			});
		}

		// start the skeleton animation
		target.trigger('startSkeleton');
	};

	/**
	 * Builds sellerHtml for every seller in the given list (will build an empty html if empty array is provided)
	 * and replaces skeletons with that built HTML
	 * see createSellerRows to understand the sellerData object structure
	 *
	 * @param targetId ID of seller-list container element
	 * @param sellerList array of sellerData objects to replace content with
	 * @param sellerHtmlBuilder
	 * @param {string} listType
	 */
	const replaceSkeletonForTab = (
		{ targetId, sellerList, isResponsesList, reactCardsVariant, listType },
		sellerType = 'project_sellers',
	) => {
		if (typeof reactCardsVariant === 'undefined') {
			reactCardsVariant = 0;
		}
		if (reactCardsVariant > 0) {
			if (isDebug()) {
				console.log(`Loading React Cards Variant: ${reactCardsVariant}`);
			}
			const sellerListFormatted = sellerListToReactCardFormat(sellerList);
			window.BuyerApp.launchModule('buyers-matches', targetId, {
				matches: sellerListFormatted,
				variant: reactCardsVariant,
				listType,
				fireExpEvent: fireExperimentTrackingEvent,
				onMount: (setSellerFn) => {
					setSeller = setSellerFn;
				},
			});
		} else {
			let sellerHtml = createSellerRows(sellerList || [], sellerType, isResponsesList);
			let target = $(`#${targetId}`);
			target.trigger('replaceSkeletonWithContent', [sellerHtml]);
		}
		showSellersImmediately();
		toggleSpinner(false);

		// show survicate survey passing category and country id so we can identify the
		try {
			let categoryId = state.projectDetail?.attributes?.category?.id || 0;
			let countryId = state.buyerDetails?.data?.country_id || 0;
			window.Bark.svc.init();
			window.Bark.svc.clearPollEvents();
			let sellerCountBucket = '0';
			if (sellerList.length >= 10) {
				sellerCountBucket = '10';
			} else if (sellerList.length >= 5) {
				sellerCountBucket = '5-9';
			} else if (sellerList.length >= 1) {
				sellerCountBucket = '1-4';
			} else {
				sellerCountBucket = '0';
			}
			window.Bark.svc.fireEvent(
				'seller-list-loaded',
				{
					category_id: categoryId,
					country_id: countryId,
					list_type: isResponsesList ? 'responses' : 'matches',
					seller_count: sellerCountBucket,
				},
				true,
			);
		} catch (e) {
			bugsnagClient.notify(e);
		}
	};

	const sellerListToReactCardFormat = (sellerList) => {
		return sellerList.map((seller) => sellerDataToReactCardFormat(seller));
	};

	const publicProfileForProject = (urlPublicPath, projectHash, bpvsid) => {
		let publicLink = `${urlPublicPath}?prid=${projectHash}&bpvid=${state.bpvid}`;
		if (bpvsid !== null && typeof bpvsid !== 'undefined') {
			publicLink = `${publicLink}&bpvsid=${bpvsid}`;
		}
		return publicLink;
	};

	const sellerDataToReactCardFormat = ({ id, attributes: seller }) => {
		let labels = [];
		seller.labels.forEach((label) => {
			for (const [labelName, state] of Object.entries(label)) {
				if (state) {
					labels.push(labelName);
				}
			}
		});
		const projectHash = getProjectHash();
		let serviceList = [];
		if (seller.top_categories) {
			serviceList = reorderAndSliceServices(seller.top_categories);
		}
		return {
			id: id,
			sellerId: id,
			company_name: seller.company_name,
			is_verified: seller.is_bark_vetted,
			review_score: seller.reviews_score,
			review_count: seller.reviews_count,
			distance: {
				value: seller.location_distance,
				unit: 'mi',
			},
			branding: seller.avatar,
			labels: labels,
			company_description: seller.profile_text,
			services: serviceList,
			public_profile_path: publicProfileForProject(
				seller.url_public_path,
				projectHash,
				seller.bpvsid,
			),
			companyAge: seller.company_age,
			hasAccreditation: seller.has_accreditation,
			lastActiveText: seller.last_online_text,
			responseTimeText: seller.median_response_time_text,
			quote: {
				status: seller.quote_status,
			},
			match_types: [
				{
					name: 'request_reply',
					data: {
						phid: projectHash,
						bpvsid: String(seller.bpvsid),
						phone: state.buyerDetails?.data?.telephone,
					},
				},
			],
		};
	};

	const reorderAndSliceServices = (sellerServices = []) => {
		let services = [];
		try {
			const currentCategory = state?.projectDetail?.attributes?.category?.name;
			services.push(currentCategory);
			sellerServices.forEach((service) => {
				if (services.length < 3) {
					if (service !== currentCategory) {
						services.push(service);
					}
				}
			});
		} catch (error) {
			bugsnagClient.notify(error);
			services = [];
		}
		return services;
	};

	/**
	 * @param sellers
	 * @param {string} listType
	 */
	const createSellerRows = (sellers, listType = 'project_sellers', isResponsesList) => {
		let listTypes = [listType];
		if (listType === 'project_sellers') {
			listTypes.push('bookable_sellers');
		}
		return sellers.map((item) => {
			if (listTypes.includes(item.type)) {
				let seller = sellerHtmlBuilder.buildSellerHtml(
					{
						...item,
						linkListType: listType,
						projectHash: getProjectHash(),
					},
					state,
					isResponsesList,
				);
				let $seller = $(seller);
				$seller.data('sellerData', { ...item.attributes, id: item.id }); // Append the data to the row for later use
				return $seller;
			}
		});
	};

	const getProjectHash = () => {
		return $('#project-id').val();
	};

	const redirectToTheProjectList = () => {
		window.location.href = _l('/buyers/dashboard/');
	};

	/**
	 * @param pageName
	 */
	const bindListeners = ({ pageName = null }) => {
		experimentTracking.initialiseForList(getProjectHash(), getSellerAnalyticsMetadata);
		if (null != pageName) experimentTracking.setPagename(pageName);
		bindSellerListListeners();
	};

	const bindSplitListListeners = () => {
		$('#feedback-gratitude-modal').on(
			'click',
			'.js-feedback-gratitude-modal-dismiss',
			function () {
				const feedbackModal = $('#feedback-gratitude-modal');
				feedbackModal.modal('hide');
			},
		);

		const splitListContainer = $('.seller-list-sort-container');
		$('#matches, #responses').click(function (e) {
			e.preventDefault();
			$(this).tab('show');
			let activeTab = e.target.closest('.nav-link').id;
			splitUrlHandler.pushUrlParamsToHistory({ activeTab });
			experimentTracking.submitExpAct(`View tab ${activeTab}`);
			handleShowingRequestQuoteFromAll({
				sellerList: state.matches.sellerList,
				...splitUrlHandler.getUrlParams(),
				lt: activeTab,
			});

			//Bind sticky header listner
			listFilters.stickyHeaderOnMobile();
		});
		splitListContainer.on('click', '.js-view-matches', () => {
			$('#matches').tab('show');
			scrollToTopOfSellerList();
			splitUrlHandler.pushUrlParamsToHistory({ activeTab: 'matches' });
			handleShowingRequestQuoteFromAll({
				sellerList: state.matches.sellerList,
				...splitUrlHandler.getUrlParams(),
				p: 1,
				lt: 'matches',
			});
			//Bind sticky header listner
			listFilters.stickyHeaderOnMobile();
		});

		splitListContainer.on('click', '.js-pagination-btn', (e) => {
			e.preventDefault();
			let targetPage = $(e.currentTarget).data('page-target');
			scrollToTopOfSellerList();
			loadAndRenderSplitPanel({ ...splitUrlHandler.getUrlParams(), p: targetPage });
			experimentTracking.submitExpAct(`View page ${targetPage}`);
			splitUrlHandler.pushUrlParamsToHistory({ page: targetPage });
		});

		splitListContainer.on('click', '.great_match-info', (e) => {
			e.preventDefault();
			const projectId = getProjectHash();
			const sellerProfileId = $(e.currentTarget).data('sellerProfileId');
			handleGreatMatchInfoClick(projectId, sellerProfileId);
			if (window.BuyerApp) {
				window.BuyerApp.launchModule('great-match', '__reqGreatMatchInfo', {
					projectId,
					sellerProfileIdHashed: sellerProfileId,
				});
			}
		});

		splitListContainer.on('click', '.js-sort-option', (e) => {
			let { lt, ls, filterParams } = splitUrlHandler.getUrlParams();
			e.preventDefault();
			let sortType = $(e.currentTarget).attr('value');
			if (sortType !== ls) {
				let sortText = $(e.currentTarget).text();
				$('.js-sort-text').text(sortText);
				// reset rqfa logic so that it can be redetermined with the new information
				window.rqfa.setShouldNotRQFA(false);
				loadAndRenderSplitPanel({ p: 1, lt, ls: sortType, filterParams });
				experimentTracking.submitExpActWithVal(`Sort by ${sortText}`, { sortType });
				splitUrlHandler.pushUrlParamsToHistory({
					page: 1,
					sort: sortType,
					filterParams,
				});
			}
		});

		$(window).on('popstate', ({ originalEvent: { state: windowState } }) => {
			if (!windowState) {
				return;
			}
			// if panel changed, switch tab
			let panelChanged =
				windowState?.lt &&
				$(`#${windowState.lt}-tab`).length &&
				!$(`#${windowState.lt}-tab`).hasClass('active');
			let pageChanged =
				windowState?.p && state.matches?.pagination?.currentPage !== windowState.p;
			let sortChanged =
				windowState?.ls &&
				windowState.ls !== $('#sortDropdown .js-sort-text').text().toLowerCase();
			if (pageChanged || panelChanged || sortChanged) {
				location.reload();
			}
		});

		eventEmitter.on('sort.changed', (args) => {
			scrollToTopOfSellerListMobileTabletOnly();
			let { lt, ls, filterParams } = splitUrlHandler.getUrlParams();
			let sortType = args.newSort;
			loadAndRenderSplitPanel({ p: 1, lt, ls: sortType, filterParams });
			splitUrlHandler.pushUrlParamsToHistory({ page: 1, sort: sortType, filterParams });
		});

		eventEmitter.on('filters.applied', (args) => {
			scrollToTopOfSellerListMobileTabletOnly();
			let currentFilters = listFilters.getCurrentFiltersAndValues();
			loadAndRenderSplitPanel({
				...splitUrlHandler.getUrlParams(),
				p: 1,
				filterParams: currentFilters,
			});
			splitUrlHandler.pushUrlParamsToHistory({ page: 1, filterParams: currentFilters });
		});

		eventEmitter.on('filters.mobile.changed', (args) => {
			let currentFilters = listFilters.getCurrentFiltersAndValues();
			updateAndDrawMobilePreviewFilters(currentFilters);
		});

		eventEmitter.on('filters.mobile.reset', (args) => {
			updateAndDrawMobilePreviewFilters({});
		});

		eventEmitter.on('filters.reset', () => {
			splitUrlHandler.removeFilterParamsFromUrl();
			loadAndRenderSplitPanel({ ...splitUrlHandler.getUrlParams(), p: 1 });
			splitUrlHandler.pushUrlParamsToHistory({
				...splitUrlHandler.getUrlParams(),
				page: 1,
			});
		});
	};

	const updateAndDrawMobilePreviewFilters = (currentFilters) => {
		let fq = splitUrlHandler.buildFilterQuery(currentFilters);
		let payload = {
			bpvid: state.bpvid,
			pageNum: 1,
			sort: 'best',
			version: 'v2',
			filterQuery: fq,
		};

		eventEmitter.emit('sellerlistloading.start');
		let matchesPromise = getMatchesForProject(payload);
		matchesPromise.then((matchesPromiseData) => {
			//Update mobile filters preview data
			listFilters.updateMobileFiltersPreviewData(matchesPromiseData);
			//Re-draw mobile preview filters
			listFilters.drawPreviewMobileFiltersModal();
			eventEmitter.emit('sellerlistloading.finish');
		});
	};

	const bindSellerListListeners = () => {
		const $sellerList = $('#project-seller-list');

		$sellerList.on('click', '.js-seller-list-back-button', handleBackButtonClick);

		// Request quote from one
		$sellerList.on('click', '.js-request-quote', function () {
			let $self = $(this);
			let sellerProfileId = $self.data('sellerProfileId');
			ctaClick('request_reply', sellerProfileId);
			if ($self.closest('.bookable-seller-card').length > 0) {
				triggerEndStateModal('request-reply', sellerProfileId);
				return;
			}
			let $sellerRow = $(`.seller-list-card[data-seller-profile-id=${sellerProfileId}]`);
			if (window.BuyerApp) {
				window.BuyerApp.launchModule('request-reply-modal', '__request-reply-modal', {
					hashedProjectId: getProjectHash(),
					bpvsId: $sellerRow.data('sellerData').bpvsid,
					sellerId: sellerProfileId,
					buyerPhone: state.buyerDetails?.data?.telephone,
					onSuccess: (r) => requestReplyRqSuccess(r, sellerProfileId),
					onClose: () => displayRequestQuoteUpsell(getProjectHash()),
					//onError,
				});
			}
		});

		$sellerList.on('actionSuccess', (event, data) => {
			let $sellerRow = $(
				`.seller-list-card[data-seller-profile-id=${data.sellerProfileId}]`,
			);

			if ($sellerRow.length) {
				let afterRequestSuccess = null;
				if (isRequestQuoteUpsellActive(getProjectHash())) {
					removeRequestedSeller(data.sellerProfileId);
					afterRequestSuccess = displayRequestQuoteUpsell;
				}
				window.rqfa.setQuoteRequested($sellerRow);
				RequestQuoteV2.openModal(
					{ ...$sellerRow.data('sellerData') },
					getProjectHash(),
					afterRequestSuccess,
				);
			}
		});

		$sellerList.on('click', '.js-view-profile-action', function (e) {
			e.preventDefault();
			let $self = $(this);
			let a = e.currentTarget;
			let sellerProfileId = $self.data('sellerProfileId');
			bookableTracking.fireTracking('list_click', {
				cta_name: 'view_profile',
				...getSellerDataForTracking(sellerProfileId),
			});
			if ($self.closest('.bookable-seller-card').length > 0) {
				redirectToUrlWithSplitListParams(a.href);
				return;
			}
			if ($self.hasClass('no-interaction')) {
				redirectToUrlWithSplitListParams(a.href);
				return;
			}

			let $sellerRow = $(`.seller-list-card[data-seller-profile-id=${sellerProfileId}]`);
			let bpvsid =
				$sellerRow.data('sellerData') &&
				$sellerRow.data('sellerData').hasOwnProperty('bpvsid')
					? $sellerRow.data('sellerData').bpvsid
					: null;
			let data = {
				type: 'profile-view',
				bpvsid,
				sellerProfileId,
				projectId: getProjectHash(),
				source: 'buyer-interaction',
			};
			buyerApi.postResponseAction(data);
			redirectToUrlWithSplitListParams(a.href);

			//Used for CSAT widget
			window.localStorage.setItem('csat-widget.viewedProfile', true);
		});

		$sellerList.on('click', '.js-last-message-container', function () {
			redirectToUrlWithSplitListParams(window.location.origin + $(this).data('href'));
		});

		$sellerList.on('click', '.js-enquiry-call-seller', function (e) {
			e.preventDefault();
			const $self = $(this);
			const sellerProfileId = $self.data('sellerProfileId');
			if ($self.closest('.bookable-seller-card').length > 0) {
				triggerEndStateModal('enquiry-call', sellerProfileId);
				return;
			}
			const trackingData = {
				...getSellerAnalyticsMetadata($self.data('sellerId')),
				from_contact_now: false,
			};
			const isLastSellerToInteractOnMarketplaceList =
				marketplaceListIsLastSellerToInteract();
			handleEnquiry(
				$self,
				trackingData,
				marketplaceInteractionCompleteCallback,
				isLastSellerToInteractOnMarketplaceList,
			);
		});

		$sellerList.on('click', '.js-transactions-book-now', function (e) {
			e.preventDefault();
			const $self = $(this);
			const sellerProfileId = $self.data('sellerProfileId');

			ctaClick('booking_request_book_now', sellerProfileId);
			let a = e.currentTarget;
			redirectToUrlWithSplitListParams(a.href);
		});

		$sellerList.on('click', '.js-transactions-book-now-requested', function (e) {
			e.preventDefault();
			const $self = $(this);
			const sellerProfileId = $self.data('sellerProfileId');

			ctaClick('booking_request_book_now_requested', sellerProfileId);
			let a = e.currentTarget;
			redirectToUrlWithSplitListParams(a.href);
		});

		$sellerList.on('click', '.js-enquiry-book-now', function (e) {
			e.preventDefault();

			const $self = $(this);
			const sellerProfileId = $self.data('sellerProfileId');
			ctaClick('enquiry_book_now', sellerProfileId);
			if ($self.closest('.bookable-seller-card').length > 0) {
				triggerEndStateModal('book-now', sellerProfileId);
				return;
			}

			const { projectDetail, buyerDetails } = state;

			const $sellerRow = $(
				`.seller-list-card[data-seller-profile-id=${sellerProfileId}]`,
			);

			const { company_name, id, sp_id } = $sellerRow.data('sellerData');

			const sellerInfo = state.matches.sellerList.find(
				(seller) => seller.id === sellerProfileId,
			);

			const isLastSellerToInteractOnMarketplaceList =
				marketplaceListIsLastSellerToInteract();

			barkTrackingLogEvent(
				'seller-list-&-profile',
				'Callbacks - Book a call - Select',
				getSellerAnalyticsMetadata(id),
			);

			handleBookNow(
				sp_id,
				company_name,
				buyerDetails.data.id,
				projectDetail.id,
				sellerProfileId,
				'seller-list',
				projectDetail.attributes.category.id,
				marketplaceInteractionCompleteCallback,
				isLastSellerToInteractOnMarketplaceList,
			);
		});

		$sellerList.on('click', '.js-contact-options-seller', function (e) {
			e.preventDefault();

			const $self = $(this);
			const isMarketplaceEnquiry = $self.data('marketplace');
			const isMarketplaceMessage = $self.data('marketplace-message');
			const sellerProfileId = $self.data('sellerId');
			const companyName = $self.data('companyName');

			const { projectDetail, buyerDetails } = state;
			const projectId = projectDetail.id;
			const buyerUserId = buyerDetails.data.id;

			const isLastSellerToInteractOnMarketplaceList =
				marketplaceListIsLastSellerToInteract();

			barkTrackingLogEvent(
				'Callbacks-&-contact-options',
				'contact now - click',
				getSellerAnalyticsMetadata(sellerProfileId),
			);

			ctaClick('contact_now', sellerProfileId);

			showContactOptionsModal(
				$self,
				sellerProfileId,
				projectId,
				buyerUserId,
				companyName,
				{
					message: isMarketplaceMessage,
					callNow: isMarketplaceEnquiry,
				},
				{
					...getSellerAnalyticsMetadata(sellerProfileId),
					from_contact_now: true,
				},
				marketplaceInteractionCompleteCallback,
				isLastSellerToInteractOnMarketplaceList,
			);
		});

		$sellerList.on('click', '.js-send-message', function (e) {
			e.preventDefault();

			const $self = $(this);
			const sellerProfileId = $self.data('sellerId');
			const companyName = $self.data('companyName');

			const { projectDetail, buyerDetails } = state;
			const projectId = projectDetail.id;
			const buyerUserId = buyerDetails.data.id;
			const isLastSellerToInteractOnMarketplaceList =
				marketplaceListIsLastSellerToInteract();

			barkTrackingLogEvent('Callbacks-&-contact-options', 'message_button', {
				...getSellerAnalyticsMetadata(sellerProfileId),
				from_contact_now: false,
			});

			showSendMessageModal(
				sellerProfileId,
				projectId,
				buyerUserId,
				companyName,
				getSellerAnalyticsMetadata(sellerProfileId),
				marketplaceInteractionCompleteCallback,
				isLastSellerToInteractOnMarketplaceList,
			);
		});

		$sellerList.on('click', '.js-booking-cancel', function (e) {
			e.preventDefault();
			const $self = $(this);
			const { bookings, projectDetail } = state;

			const bookingId = $self.data('booking');
			const sellerHashId = $self.data('seller-id');
			const sellerId = $self.data('spid');

			const { attributes, id: responseId } = bookings.find(
				({ id }) => id.toString() === bookingId.toString(),
			);
			const { buyer_id, project_id } = attributes;

			barkTrackingLogEvent('Callbacks-&-cancel', 'Cancel callback - Select', {
				Project_id: project_id,
				seller_profile_id: sellerHashId,
				Category_id: projectDetail.attributes.category.id,
				buyer_user_id: buyer_id,
				response_id: responseId,
			});

			handleBookingCancellation({
				booking_id: bookingId,
				seller_id: sellerId,
				buyer_id: buyer_id,
				project_id: project_id,
				seller_profile_hash_id: sellerHashId,
				response_id: responseId,
				category_id: projectDetail.attributes?.category?.id,
			});
		});
	};

	/**
	 * Use this to fire any actions directly after the project has been received/handled
	 *
	 */
	const initAfterBarkActions = () => {
		handleProjectRejectedState(state.projectDetail);
		if (!hasProjectFromSellerProfile()) {
			showAdditionalDetailsIfNeededAfterBark(
				{
					firstView: isFirstProjectView(state.projectDetail),
					project: state.projectDetail,
				},
				null,
			);
		}
		setProjectTitle(state.projectDetail);
	};

	const handleProjectRejectedState = (project) => {
		if (!project.attributes?.state?.is_active) {
			location.reload();
		}
	};

	const handleProjectDataLoadedSteps = (initialLoadPromises) => {
		initAfterBarkActions();
		handleProjectDataLoadedExperiments();
		state.bpvid = state.projectDetail.attributes.bpvid;
		if (state.projectDetail.attributes?.wait_for_suggestion_rank) {
			// if we should wait for the suggestion rank to be created, we should wait no longer than 10 seconds
			// from the project being created, that will catch the vast majority of cases
			let t = new Date(state.projectDetail.attributes?.created);
			t.setSeconds(t.getSeconds() + 5);
			initialLoadPromises.push(new Promise((resolve) => waitUntilDateTime(t, resolve)));
		} else {
			// if we shouldn't wait, just put a resolved promise in so it resolves instantly
			initialLoadPromises.push(Promise.resolve());
		}
	};

	/** Begin browse section **/

	const loadAndRenderBrowse = ({ p, ls }) => {
		let targetId = `seller-list`;
		startSkeletonForTab({ targetId });
		getResponsesForBrowseList({ p, ls }, (res) => {
			setBrowseList(res);
			replaceSkeletonForTab({ targetId, sellerList: state.browseList }, 'browse_sellers');
			matchesPromiseHandler(res);
			renderBrowsePagination('project-seller-list');
			TenderUpsellModal(false);
		});
	};

	const setBrowseListTitle = (title, elementId = '.js-project-title') => {
		$(elementId).text(title);
	};

	const handleBrowseDataLoadedSteps = () => {
		setBrowseListTitle(state.browseListTitle);
		handleBrowseDataLoadedExperiments();
		startSkeletonLoading();
	};
	/**
	 * When project is loaded from API, it will contain data about active experiments for this browse user
	 * If they have any setup functionality needed, put it here
	 */
	const handleBrowseDataLoadedExperiments = () => {
		if (
			isFirstProjectView(state.projectDetail) &&
			state.projectDetail?.attributes?.experiments &&
			state.projectDetail.attributes.experiments[experimentName]
		) {
			experimentTracking.submitExpActWithVal('Experiment Allocation - Browse list', {
				variant: getExperimentVariant(),
			});
		}
	};

	/**
	 * @param {string} containerId
	 */
	const renderBrowseListPage = (containerId = 'seller-list') => {
		toggleLoading(false);
		$('#' + containerId).trigger('removeAllSkeletonsFromContainer');
	};

	const renderBrowsePagination = (containerId) => {
		const paginationRowContainer = $(`#${containerId} .pagination-row-container`);
		let paginationTemplate = Handlebars.compile($('#pagination-row').html());
		paginationRowContainer.html(
			paginationTemplate({
				nextPageNumber: state.matches?.pagination?.nextPageNumber,
				prevPageNumber: state.matches?.pagination?.prevPageNumber,
				currentPage: state.matches?.pagination?.currentPage,
				totalPages: state.matches?.pagination?.totalPages,
			}),
		);
	};

	/**
	 * @param projectListElementId
	 * @param upsellModal
	 */
	const getBrowseDataForPageAndRender = (
		projectListElementId = 'project-seller-list',
		upsellModal = null,
	) => {
		let browsePromise = Promise.resolve(state.browseList);
		renderBrowseListPage();
		browsePromise
			.then(({ data: ListData, meta: sellerListMeta }) => {
				matchesPromiseHandler(state.browseList);
				state.fullSellerList = ListData;
				state.moreSuggestionsAvailable = sellerListMeta?.pagination ?? false;

				// Page render 🖼
				renderPage(false, 'browse_sellers', false);
				renderBrowsePagination('project-seller-list');

				ListData.length > 0
					? $('.js-has-results').removeClass('d-none')
					: $('.js-has-results').addClass('d-none');
				ListData.length === 0
					? $('.js-no-results').removeClass('d-none')
					: $('.js-no-results').addClass('d-none');

				$('#' + projectListElementId).trigger('initialSellersLoaded', [ListData.length]);
				submitLoadingEventsForExperiments(
					ListData.length > 0 || sellerListMeta?.moreSuggestionsAvailable,
				);
				bindBrowseListListeners();
				let { ls } = splitUrlHandler.getUrlParams();
				setSelectedSort(ls);
				if (upsellModal) {
					// This is an all in one function, could really be split out
					upsellModal();
				}
			})
			.catch(handleSellerListError);
	};

	const browseErrorRender = () => {
		renderBrowseListPage();
		$('.js-has-results').addClass('d-none');
		$('.js-no-results').removeClass('d-none');
	};

	const setBrowseList = (browseList) => {
		state.browseList = browseList.data;
	};

	const bindBrowseListListeners = () => {
		const splitListContainer = $('.default-seller-list-container');
		splitListContainer
			.off('click.paginate')
			.on('click.paginate', '.js-pagination-btn', (e) => {
				e.preventDefault();
				let targetPage = $(e.currentTarget).data('page-target');
				scrollToTopOfSellerList();
				const { ls } = splitUrlHandler.getUrlParams();
				loadAndRenderBrowse({ ls, p: targetPage });
				splitUrlHandler.pushUrlParamsToHistory({ page: targetPage, sort: ls });
			});
		splitListContainer
			.off('click.sorting')
			.on('click.sorting', '.js-sort-option', (e) => {
				let { p, ls } = splitUrlHandler.getUrlParams();
				e.preventDefault();
				let sortType = $(e.currentTarget).attr('value');
				if (sortType !== ls) {
					let sortText = $(e.currentTarget).text();
					$('.js-sort-text').text(sortText);
					browseFunctions.tracking(`Sort by ${sortText}`, { sortType });
					loadAndRenderBrowse({ p: 1, ls: sortType });
					splitUrlHandler.pushUrlParamsToHistory({ page: 1, sort: sortType });
				}
			});

		$(window).on('popstate', ({ originalEvent: { state: windowState } }) => {
			if (!windowState) {
				return;
			}
			let pageChanged =
				windowState?.p && state.matches?.pagination?.currentPage !== windowState.p;

			if (pageChanged) {
				scrollToTopOfSellerList();
				return loadAndRenderBrowse({ p: pageChanged });
			}
		});
	};

	/**
	 * @param pageName
	 */
	const bindBrowseListeners = ({ pageName = null }) => {
		experimentTracking.initialiseForList(
			getProjectHash(),
			getSellerAnalyticsMetadata,
			'Browse-list',
		);
		if (null != pageName) experimentTracking.setPagename(pageName);
		bindSellerListListeners();
	};

	const requestReplyRqSuccess = (response, sellerProfileId) => {
		let $sellerRow = $(`.seller-list-card[data-seller-profile-id=${sellerProfileId}]`);

		if ($sellerRow.length) {
			if (isRequestQuoteUpsellActive(getProjectHash())) {
				removeRequestedSeller(sellerProfileId);
			}
			window.rqfa.setQuoteRequested($sellerRow);
		}
	};

	const marketplaceInteractionCompleteCallback = (
		marketplaceType,
		sellerProfileId,
		meta,
	) => {
		if (enquiriesSellersOnlyList.isShowing()) {
			enquiriesSellersOnlyList.onMarketplaceInteractionComplete(
				state,
				marketplaceType,
				sellerProfileId,
				meta,
			);
		}
	};

	const marketplaceListIsLastSellerToInteract = () => {
		return (
			enquiriesSellersOnlyList.isShowing() &&
			enquiriesSellersOnlyList.isLastSellerToInteract(state)
		);
	};

	/** End browse section **/

	return {
		addManagedSkeletonContainer,
		handleBrowseDataLoadedSteps,
		handleProjectDataLoadedSteps,
		handleProjectDataLoadedExperiments,
		getExperimentVariant,
		handleInitialLoading,
		getBrowseDataForPageAndRender,
		getDataForSellerPageAndRender,
		bindListeners,
		bindBrowseListeners,
		getProjectHash,
		getHashedSessionId,
		browseErrorRender,
		redirectToTheProjectList,
	};
}
